/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useDevice } from '@squiz/dxp-console-support';
import { NavigationSection, SkipLinks, SkipLinksProps, Toolbar } from '@squiz/sds';
import { useDxpFrameStore } from 'dxpConsoleCore-mf/State';
import React from 'react';
import { DxpCapability } from '../../../../Constants/DxpCapabilities/defs/DxpCapability.defs';
import { DXP_SKIP_LINKS } from '../../../../Constants/NavItems.constants';
import { DXP_MENU_RESOURCES_SECTION, DXP_MENU_USER_ACCOUNT_SECTION } from '../../../../Constants/NavSections.constants';
import { useAccountOptions } from '../../../../Hooks/UseAccountOptions/UseAccountOptions.hook';
import { useToolbarConfiguration } from '../../../../Hooks/UseToolbarConfiguration/UseToolbarConfiguration.hook';
import { FrameHeaderProps } from './FrameHeader.props';

/**
 * DXP Console Frame header.
 */
export const FrameHeader = (props: FrameHeaderProps): JSX.Element => {
  const { toolbarProps } = props;
  const {
    capability,
    navigationSections,
    showHeader,
  }: {
    capability: DxpCapability | null;
    navigationSections: Array<NavigationSection>;
    showHeader: boolean;
  } = useDxpFrameStore();

  // Device detection
  const deviceType = useDevice();

  // Set up fixed menu sections
  const dxpAccountMenu = DXP_MENU_USER_ACCOUNT_SECTION;
  dxpAccountMenu.items = useAccountOptions();
  const menuSections = [DXP_MENU_RESOURCES_SECTION, dxpAccountMenu];

  // Join current menu items with fixed items
  const allSections = [...navigationSections, ...menuSections];

  // Filter menu items depending on the device type
  const filteredMenuSections = allSections.filter((section) => {
    const allowedDevices = section.devices || [];

    // Return sections that are scoped for the current device
    if (allowedDevices.length > 0) {
      return allowedDevices.indexOf(deviceType) !== -1;
    }

    // By default, return sections that are allowed everywhere
    return section;
  });

  // Configure toolbar
  const configuredToolbarProps = useToolbarConfiguration(toolbarProps);
  configuredToolbarProps.menuSections = filteredMenuSections;
  configuredToolbarProps.showTabletNavigation = filteredMenuSections.length > 0;

  if (capability) {
    configuredToolbarProps.text = capability.title;
  }

  // Configure skip links
  const skipProps: SkipLinksProps = {
    linkList: DXP_SKIP_LINKS,
  };

  return (
    <>
      <SkipLinks {...skipProps} />
      {showHeader && (
        <header className="header" data-tablet-nav={configuredToolbarProps.showTabletNavigation}>
          <Toolbar {...configuredToolbarProps} />
        </header>
      )}
    </>
  );
};
